import axios, { AxiosError, AxiosHeaders } from 'axios';
import { toast } from 'sonner';
import { storage } from '../lib/storage';
import type { ApiEnvelope } from './types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60_000,
});

// Public PDF tool endpoints that should NOT trigger auto-logout on 401.
// See PROMPT.md §5.6.
const PUBLIC_PATHS = [
  '/api/pdf/compress',
  '/api/pdf/split',
  '/api/pdf/merge',
];

function isPublicPath(url?: string): boolean {
  if (!url) return false;
  return PUBLIC_PATHS.some((p) => url.startsWith(p));
}

apiClient.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    // Server expects raw token without "Bearer" prefix (PROMPT.md §5.1).
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', token);
    config.headers = headers;
  }
  return config;
});

let onUnauthorizedHandler: (() => void) | null = null;

export function registerUnauthorizedHandler(fn: () => void): void {
  onUnauthorizedHandler = fn;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    const status = error.response?.status;
    const url = error.config?.url;

    if (status === 401 && !isPublicPath(url)) {
      storage.clear();
      onUnauthorizedHandler?.();
      toast.error('Сессия истекла. Войдите снова.');
    } else if (status === 413) {
      // Leave to the caller — typically surfaced as inline validation.
    } else if (status && status >= 500) {
      toast.error('Что-то пошло не так. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('[api 5xx]', url, error.response?.data);
    } else if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
      toast.error('Проверьте соединение.');
    }
    return Promise.reject(error);
  },
);

export function extractErrorMessage(error: unknown, fallback = 'Неизвестная ошибка'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiEnvelope<unknown> | undefined;
    if (data?.message) return data.message;
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

const SUCCESS_STATUSES = new Set(['OK', 'Success']);

export function unwrap<T>(envelope: ApiEnvelope<T>): T {
  const ok = SUCCESS_STATUSES.has(String(envelope.status));
  // == null ловит и null, и undefined. У эндпоинтов, которые легитимно
  // возвращают `data: null` (signup/restore-confirm), мы unwrap не вызываем —
  // там ответ игнорируется.
  if (!ok || envelope.data == null) {
    throw new Error(envelope.message || 'Ошибка сервера');
  }
  return envelope.data;
}
